import { api, ApiError, type CursorPage } from '@/api/client';
import type {
  ApiKey,
  ApiKeyCreated,
  Certification,
  LicenseOcr,
  Line,
  Member,
  OperationLog,
  RoleCatalog,
  SupportContact,
  SystemMessage,
  TenantBrief,
} from '@/api/types.contract';

/* —— 企业 —— */

export const listTenants = (signal?: AbortSignal) =>
  api.get<{ items: TenantBrief[] }>('/auth/tenants', signal ? { signal } : {});

export interface MemberQuery {
  keyword?: string | undefined;
  status?: string | undefined;
  role?: string | undefined;
}

export const listMembers = (
  p: MemberQuery & { cursor?: string | undefined; limit?: number },
  signal?: AbortSignal,
) =>
  api.get<CursorPage<Member>>('/tenant/members', {
    query: { ...p },
    ...(signal ? { signal } : {}),
  });

export const inviteMember = (body: {
  phone: string;
  roles: string[];
  nickname?: string;
  remark?: string;
}) => api.post<Member>('/tenant/members', { body });

/** 角色 / 状态 / 备注 / 昵称任意组合；服务端逐项过守卫，任一项被拒就都不改 */
export const patchMember = (
  id: string,
  body: { roles?: string[]; status?: string; remark?: string; nickname?: string },
) => api.patch<void>(`/tenant/members/${id}`, { body });

export const removeMember = (id: string) => api.del<void>(`/tenant/members/${id}`);

/** 响应里的 temp_password 是唯一一次明文，必须立刻交给 SecretOnce */
export const resetMemberPassword = (id: string) =>
  api.post<{ temp_password: string }>(`/tenant/members/${id}/reset-password`);

export const forceLogoutMember = (id: string) =>
  api.post<void>(`/tenant/members/${id}/force-logout`);

/** 不做口令复核（老板拍板）；成功后双方所有设备下线，调用方负责把自己带回登录页 */
export const transferOwner = (body: { target_member_id: string }) =>
  api.post<void>('/tenant/transfer-owner', { body });

export const listRoles = (signal?: AbortSignal) =>
  api.get<RoleCatalog>('/tenant/roles', signal ? { signal } : {});

/* —— 企业认证 —— */

export const getCertification = (signal?: AbortSignal) =>
  api.get<Certification>('/tenant/certification', signal ? { signal } : {});

/**
 * 提交认证。`license_file_url` 必须是上传接口返回的引用，
 * 不把文件塞进 JSON —— 营业执照扫描件动辄几 MB。
 */
export const submitCertification = (body: {
  company_name: string;
  license_no: string;
  legal_person: string;
  license_file_url: string;
}) => api.post<Certification>('/tenant/certification', { body });

export async function uploadCertificationLicense(
  file: File,
  accessToken: string | null,
): Promise<{ license_file_url: string }> {
  const form = new FormData();
  form.append('file', file);
  const headers = new Headers();
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  headers.set('Idempotency-Key', crypto.randomUUID());
  headers.set('X-Request-Id', crypto.randomUUID());
  const res = await fetch(
    `${import.meta.env['VITE_API_BASE'] ?? '/console/v1'}/tenant/certification/license`,
    {
      method: 'POST',
      headers,
      body: form,
      credentials: 'same-origin',
    },
  );
  if (!res.ok) {
    let message = '营业执照上传失败';
    let code = `http_${res.status}`;
    try {
      const body = (await res.json()) as { error?: { code?: string; message?: string } };
      if (body.error?.message) message = body.error.message;
      if (body.error?.code) code = body.error.code;
    } catch {
      // 非 JSON 网关错误，保留兜底文案
    }
    throw new ApiError({ code, message, httpStatus: res.status });
  }
  return (await res.json()) as { license_file_url: string };
}

/** 营业执照 OCR 预识别，识别不出的字段返回 null，前端不猜。 */
export const ocrLicense = (body: { license_file_url: string }) =>
  api.post<LicenseOcr>('/tenant/certification/ocr', { body });

/** 客服入口。未配键时字段为 null，不是假号码。 */
export const getSupport = (signal?: AbortSignal) =>
  api.get<SupportContact>('/support', signal ? { signal } : {});

/* —— 资源 —— */

export const listLines = (signal?: AbortSignal) =>
  api.get<CursorPage<Line>>('/lines', signal ? { signal } : {});

/* —— 密钥 —— */

export const listApiKeys = (p: { cursor?: string | undefined }, signal?: AbortSignal) =>
  api.get<CursorPage<ApiKey>>('/api-keys', { query: { ...p }, ...(signal ? { signal } : {}) });

/** 响应里的 secret 是**唯一一次**明文（spec 06 §4），必须立刻交给 SecretOnce 弹层。 */
export const createApiKey = (body: {
  name: string;
  scopes: string[];
  employee_ids: string[];
}) => api.post<ApiKeyCreated>('/api-keys', { body });

export const revokeApiKey = (id: string) => api.post<void>(`/api-keys/${id}/revoke`);

/* —— 系统 —— */

export const listOperationLogs = (p: { cursor?: string | undefined }, signal?: AbortSignal) =>
  api.get<CursorPage<OperationLog>>('/operation-logs', {
    query: { ...p },
    ...(signal ? { signal } : {}),
  });

export const listMessages = (p: { cursor?: string | undefined }, signal?: AbortSignal) =>
  api.get<CursorPage<SystemMessage>>('/messages', {
    query: { ...p },
    ...(signal ? { signal } : {}),
  });

export const getUnreadCount = (signal?: AbortSignal) =>
  api.get<{ count: number }>('/messages/unread-count', signal ? { signal } : {});
